import { HasXY, Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { Graphic, GraphicDataAttr } from './Graphic';

const fitInBounds = (bounds: Rect, rect: Rect) => {
    // If rect is too big, proportionally scale down, then position in center
    const scale = Math.min(bounds.width / rect.width, bounds.height / rect.height);
    const scaled = rect.scale(scale);
    // Calculate center position
    const left = bounds.left + (bounds.width - scaled.width) / 2;
    const top = bounds.top + (bounds.height - scaled.height) / 2;
    return scaled.offset(new Point(left - scaled.left, top - scaled.top)).round();
};

const measureSource = (source: HTMLImageElement) => {
    if (source.width && source.height) {
        return new Rect(0, 0, source.width, source.height);
    }
    else {
        // In some cases, SVGs need to be mounted to get the width/height
        try {
            source.remove();
            document.body.appendChild(source);
            source.style.position = 'fixed';
            source.style.top = '-100000px';
            source.style.left = '-100000px';
            return new Rect(0, 0, source.width, source.height);
        }
        finally {
            source.remove();
        }
    }
};

const renderImage = (source: HTMLImageElement, elmtBounds: Rect, shouldFit: boolean) => {

    const imgBounds = measureSource(source);
    const fitRect = shouldFit ? fitInBounds(elmtBounds, imgBounds) : elmtBounds;

    const image = document.createElement('canvas');
    image.width = elmtBounds.width;
    image.height = elmtBounds.height;

    const ctx = image.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(source, fitRect.left, fitRect.top, fitRect.width, fitRect.height);
    return image;
};

export class GraphicTexture {

    public static for(graphic: Graphic): GraphicTexture {
        try {
            const { img: source } = GraphicDataAttr.get(graphic) ?? {};
            if (!source) return null;

            // If over a certain area, ignore
            if (source.width * source.height > (4096 * 4096)) {
                return null;
            }

            const shouldFit = source.src.endsWith('.svg');
            const elmtBounds = new Rect(0, 0, graphic.width, graphic.height);
            const image = renderImage(source, elmtBounds, shouldFit);

            return new GraphicTexture(image);
        }
        catch (e) {
            console.error(e);
            return null;
        }
    }

    constructor(public image: HTMLCanvasElement) {
    }

    public get width() {
        return this.image.width;
    }

    public get height() {
        return this.image.height;
    }

    public getPixel({ x, y }: HasXY) {

        x = Math.round(x);
        y = Math.round(y);

        const ctx = this.image.getContext('2d', { willReadFrequently: true });
        const { data } = ctx.getImageData(x, y, 1, 1);

        const pixel = {
            red: data[0],
            green: data[1],
            blue: data[2],
            alpha: data[3],
        };

        return pixel;
    };

    public createSilhouette(overflow?: number, color?: string) {
        overflow = overflow || 3;
        color = color || Colors.primary;

        const canvas = document.createElement('canvas');
        canvas.width = this.image.width + 100;
        canvas.height = this.image.height + 100;

        const ctx = canvas.getContext('2d');
        ctx.translate(50, 50);

        const s = overflow;
        const x = 0;
        const y = 0;

        for (let i = 0; i < 180; i += 2) {
            ctx.drawImage(this.image, x + Math.sin(i) * s, y + Math.cos(i) * s);
        }

        ctx.resetTransform();
        ctx.globalCompositeOperation = 'source-in';
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        return canvas;
    }
}

// const isIgnoredColor = (arr: number[] | Uint8ClampedArray | Uint8Array, i: number) => {
//     // ignore transparent
//     if (arr[i + 3] < 1) return true;
//     // ignore white
//     if (arr[i] === 255 && arr[i + 1] === 255 && arr[i + 2] === 255) return true;
//     // ignore black
//     if (arr[i] === 0 && arr[i + 1] === 0 && arr[i + 2] === 0) return true;
//     return false;
// };

// export function dominantAlgorithm(arr: number[] | Uint8ClampedArray | Uint8Array, len: number) {
//     const colorHash: Record<string, number[]> = {};
//     const divider = 24;
//     const step = 4;
//     let max = [0, 0, 0, 0, 0];

//     for (let i = 0; i < arr.length; i += step) {
//         const red = arr[i];
//         const green = arr[i + 1];
//         const blue = arr[i + 2];
//         const alpha = arr[i + 3];

//         if (isIgnoredColor(arr, i)) {
//             continue;
//         }

//         const key = Math.round(red / divider) + ',' +
//             Math.round(green / divider) + ',' +
//             Math.round(blue / divider);

//         if (colorHash[key]) {
//             colorHash[key] = [
//                 colorHash[key][0] + red * alpha,
//                 colorHash[key][1] + green * alpha,
//                 colorHash[key][2] + blue * alpha,
//                 colorHash[key][3] + alpha,
//                 colorHash[key][4] + 1
//             ];
//         }
//         else {
//             colorHash[key] = [red * alpha, green * alpha, blue * alpha, alpha, 1];
//         }

//         if (max[4] < colorHash[key][4]) {
//             max = colorHash[key];
//         }
//     }

//     const redTotal = max[0];
//     const greenTotal = max[1];
//     const blueTotal = max[2];

//     const alphaTotal = max[3];
//     const count = max[4];

//     return alphaTotal ? [
//         Math.round(redTotal / alphaTotal),
//         Math.round(greenTotal / alphaTotal),
//         Math.round(blueTotal / alphaTotal),
//         Math.round(alphaTotal / count)
//     ] : null;
// }
